import React, { useEffect, useState } from 'react'

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
const DAY_LABELS = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
}
const DAY_SHORT = {
  monday: 'mon',
  tuesday: 'tue',
  wednesday: 'wed',
  thursday: 'thu',
  friday: 'fri',
  saturday: 'sat',
  sunday: 'sun',
}

export const ADVANCE_OPTIONS = [
  { label: 'Same-Day', value: 0 },
  { label: 'Next-Day', value: 1 },
  { label: '2 Days', value: 2 },
  { label: '3 Days', value: 3 },
  { label: '4 Days', value: 4 },
  { label: '5 Days', value: 5 },
  { label: '6 Days', value: 6 },
  { label: '7 Days', value: 7 },
]

const DEFAULT_DAY_STATES = Object.fromEntries(DAYS.map((d) => [d, true]))

export function parseSchedule(schedule) {
  const dayStates = {}
  const closingTimes = {}

  DAYS.forEach((day) => {
    const short = DAY_SHORT[day]
    const entry = schedule?.[short] ?? { open: true, closes_at: null }
    dayStates[day] = entry.open ?? true
    closingTimes[day] = entry.closes_at ?? '17:00'
  })

  return { dayStates, closingTimes }
}

export function buildSchedule(dayStates, closingTimes, isSameDay) {
  return Object.fromEntries(
    DAYS.map((day) => {
      const short = DAY_SHORT[day]
      const open = dayStates[day]
      return [short, { open, closes_at: isSameDay && open ? closingTimes[day] : null }]
    })
  )
}

// Accepts loadFn (no args, returns promise resolving to { data: { advance_notice_days, acceptance_schedule } })
// and saveFn (params, returns promise).
export default function MailroomSettingsForm({ loadFn, saveFn }) {
  const [advanceNoticeDays, setAdvanceNoticeDays] = useState(1)
  const [dayStates, setDayStates] = useState(DEFAULT_DAY_STATES)
  const [closingTimes, setClosingTimes] = useState(Object.fromEntries(DAYS.map((d) => [d, '17:00'])))
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    loadFn()
      .then(({ data }) => {
        setAdvanceNoticeDays(data.advance_notice_days ?? 1)
        const { dayStates: parsed, closingTimes: parsedTimes } = parseSchedule(data.acceptance_schedule)
        setDayStates(parsed)
        setClosingTimes(parsedTimes)
      })
      .catch(() => {
        setError('Failed to load settings. Please refresh the page before making changes.')
        setLoadFailed(true)
      })
      .finally(() => setLoading(false))
  }, [])

  const isSameDay = advanceNoticeDays === 0

  const handleAdvanceChange = (e) => {
    setAdvanceNoticeDays(parseInt(e.target.value, 10))
    setSaved(false)
  }

  const handleDayToggle = (day) => {
    setDayStates((prev) => ({ ...prev, [day]: !prev[day] }))
    setSaved(false)
  }

  const handleClosingTimeChange = (day, value) => {
    setClosingTimes((prev) => ({ ...prev, [day]: value }))
    setSaved(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSaved(false)

    try {
      await saveFn({
        advance_notice_days: advanceNoticeDays,
        acceptance_schedule: buildSchedule(dayStates, closingTimes, isSameDay),
      })
      setSaved(true)
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save settings.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="dome-color-med-grey">Loading...</div>
  }

  return (
    <form onSubmit={handleSubmit} className="dome-d-flex dome-flex-column dome-gap24">
      <div className="dome-d-flex dome-flex-column dome-gap18">
        <label className="dome-p2 dome-color-dark-grey" htmlFor="advance_notice_days">
          How far in advance do you accept mailing requests?
        </label>
        <select
          id="advance_notice_days"
          className="form-control"
          value={advanceNoticeDays}
          onChange={handleAdvanceChange}
          style={{ maxWidth: 240 }}
        >
          {ADVANCE_OPTIONS.map(({ label, value }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="dome-d-flex dome-flex-column dome-gap18">
        <label className="dome-p2 dome-color-dark-grey">Accept mailing requests</label>
        <div className="dome-d-flex dome-flex-column dome-gap12">
          {DAYS.map((day) => (
            <div className="dome-d-flex dome-align-center dome-gap24" key={day}>
              <label style={{ margin: 0 }} className="dome-d-flex dome-align-center dome-gap12 dome-cursor-pointer">
                <input
                  type="checkbox"
                  checked={dayStates[day]}
                  onChange={() => handleDayToggle(day)}
                  style={{ margin: 0 }}
                />
                <span className="dome-p2 dome-color-dark-grey" style={{ minWidth: 100 }}>
                  {DAY_LABELS[day]}
                </span>
                <span
                  className={`dome-p3 ${dayStates[day] ? 'dome-color-dark-grey dome-text-w500' : 'dome-color-med-grey'}`}
                  style={{ fontStyle: 'italic' }}
                >
                  {dayStates[day] ? 'Open' : 'Closed'}
                </span>
              </label>
              {isSameDay && dayStates[day] && (
                <div className="dome-d-flex dome-align-center dome-gap12">
                  <span className="dome-p3 dome-color-dark-grey">Closes at</span>
                  <input
                    type="time"
                    className="form-control"
                    value={closingTimes[day]}
                    onChange={(e) => handleClosingTimeChange(day, e.target.value)}
                    style={{ width: 130, margin: 0 }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="dome-d-flex dome-flex-column dome-gap12">
        {error && <span className="dome-color-red-alert dome-p2">{error}</span>}
        {saved && <span className="dome-color-green-darker dome-p2">Settings saved.</span>}
        <div>
          <button type="submit" className="dome-btn dome-btn-base dome-btn-aqua-stroke" disabled={saving || loadFailed}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </form>
  )
}
